import React, { createContext, useState, useContext, useCallback, useRef } from 'react';
import axiosInstance from '../utils/axiosInstance';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import MinimizeIcon from '@mui/icons-material/Minimize';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';

const DocumentUploadContext = createContext();

export const useDocumentUpload = () => {
  return useContext(DocumentUploadContext);
};

const ensureUrlProtocol = (url) => {
  if (!url) return '';
  if (!/^(https?:\/\/|mailto:|tel:)/i.test(url)) {
    return 'https://' + url;
  }
  return url;
};

// --- Upload Progress Modal Component ---
function UploadProgressModal({ isOpen, onRequestClose, uploadResults, isUploading }) {
  const [isMinimized, setIsMinimized] = useState(false);

  const totalTasks = uploadResults.length;
  const completedTasks = uploadResults.filter(
    (r) => r.status === 'success' || r.status === 'error'
  ).length;
  const progressValue = totalTasks === 0 ? 0 : (completedTasks / totalTasks) * 100;
  const allDone = completedTasks === totalTasks && totalTasks > 0;

  if (!isOpen) return null;

  if (isMinimized) {
    return (
      <Paper
        elevation={6}
        sx={{
          position: 'fixed',
          bottom: 20,
          right: 20,
          zIndex: 1400,
          p: 1.5,
          px: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          minWidth: 300,
          cursor: 'pointer',
        }}
        onClick={() => setIsMinimized(false)}
      >
        {!allDone && <CircularProgress size={18} />}
        {allDone && <CheckCircleIcon color="success" fontSize="small" />}
        <Typography variant="body2" sx={{ flexGrow: 1 }}>
          Upload: {completedTasks}/{totalTasks} files
        </Typography>
        <IconButton size="small" onClick={(e) => { e.stopPropagation(); setIsMinimized(false); }}>
          <OpenInFullIcon fontSize="small" />
        </IconButton>
      </Paper>
    );
  }

  return (
    <Dialog
      open={isOpen}
      onClose={allDone ? onRequestClose : undefined}
      fullWidth
      maxWidth="sm"
      disableEscapeKeyDown={!allDone}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Upload & Training Progress</Typography>
        <Box>
          <IconButton size="small" onClick={() => setIsMinimized(true)} title="Minimize">
            <MinimizeIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              {allDone ? 'All files processed' : `Processing ${completedTasks + 1} of ${totalTasks}...`}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {Math.round(progressValue)}%
            </Typography>
          </Box>
          <LinearProgress variant="determinate" value={progressValue} sx={{ height: 8, borderRadius: 4 }} />
        </Box>
        <List dense sx={{ maxHeight: '50vh', overflowY: 'auto' }}>
          {uploadResults.map((result, index) => (
            <ListItem key={index} divider sx={{ py: 1 }}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                {result.status === 'pending' && <HourglassEmptyIcon color="disabled" />}
                {result.status === 'uploading' && <CircularProgress size={20} />}
                {result.status === 'success' && <CheckCircleIcon color="success" />}
                {result.status === 'error' && <ErrorIcon color="error" />}
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                    {result.filename}
                  </Typography>
                }
                secondary={
                  result.message && (
                    <Typography
                      variant="caption"
                      color={result.status === 'error' ? 'error' : 'success.main'}
                    >
                      {result.message}
                    </Typography>
                  )
                }
              />
              <Chip
                size="small"
                label={
                  result.status === 'pending'
                    ? 'Pending'
                    : result.status === 'uploading'
                    ? 'Uploading...'
                    : result.status === 'success'
                    ? 'Done'
                    : 'Failed'
                }
                color={
                  result.status === 'success'
                    ? 'success'
                    : result.status === 'error'
                    ? 'error'
                    : 'default'
                }
                variant="outlined"
              />
            </ListItem>
          ))}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={onRequestClose} disabled={!allDone}>
          {allDone ? 'Close' : 'Processing...'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export const DocumentUploadProvider = ({ children }) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState([]);
  const [uploadProgressOpen, setUploadProgressOpen] = useState(false);
  const onCompleteRef = useRef(null);
  const isUploadingRef = useRef(false);

  const registerOnComplete = useCallback((callback) => {
    onCompleteRef.current = callback;
  }, []);

  const unregisterOnComplete = useCallback(() => {
    onCompleteRef.current = null;
  }, []);

  const closeUploadProgress = useCallback(() => {
    setUploadProgressOpen(false);
    setUploadResults([]);
  }, []);

  const startFileUpload = useCallback(async (files, chatbotId, categoryIds, websiteLink) => {
    if (isUploadingRef.current) return;
    isUploadingRef.current = true;
    setIsUploading(true);

    // files should already be an Array (converted from FileList by the caller)
    const fileList = Array.from(files);

    const initialResults = fileList.map((file) => ({
      filename: file.name,
      status: 'pending',
      message: '',
    }));
    setUploadResults(initialResults);
    setUploadProgressOpen(true);

    const params = chatbotId ? { chatbot_id: chatbotId } : {};
    let currentResults = [...initialResults];

    for (let i = 0; i < fileList.length; i++) {
      currentResults[i].status = 'uploading';
      currentResults[i].message = 'Uploading and processing...';
      setUploadResults([...currentResults]);

      const formData = new FormData();
      formData.append('files', fileList[i]);
      formData.append('category_ids', JSON.stringify(categoryIds || []));
      if (websiteLink && websiteLink.trim()) {
        formData.append('website_link', ensureUrlProtocol(websiteLink.trim()));
      }

      try {
        const response = await axiosInstance.post('/upload-private-data', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          params,
        });

        currentResults[i].status = 'success';
        currentResults[i].message = response.data.message || 'Uploaded & trained successfully';
      } catch (err) {
        currentResults[i].status = 'error';
        currentResults[i].message =
          err.response?.data?.message || err.message || 'Upload failed';
      }

      setUploadResults([...currentResults]);
    }

    isUploadingRef.current = false;
    setIsUploading(false);

    if (onCompleteRef.current) {
      onCompleteRef.current();
    }
  }, []);

  const startDocumentUpload = useCallback(async (file, chatbotId, categoryIds, websiteLink) => {
    if (isUploadingRef.current) return;
    isUploadingRef.current = true;
    setIsUploading(true);

    const singleResult = [{ filename: file.name, status: 'uploading', message: 'Uploading and processing...' }];
    setUploadResults(singleResult);
    setUploadProgressOpen(true);

    const formData = new FormData();
    formData.append('files', file);
    formData.append('category_ids', JSON.stringify(categoryIds || []));
    if (websiteLink && websiteLink.trim()) {
      formData.append('website_link', ensureUrlProtocol(websiteLink.trim()));
    }

    const params = chatbotId ? { chatbot_id: chatbotId } : {};

    try {
      const response = await axiosInstance.post('/upload-private-data', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        params,
      });

      singleResult[0].status = 'success';
      singleResult[0].message = response.data.message || 'Created & trained successfully';
      setUploadResults([...singleResult]);
    } catch (err) {
      singleResult[0].status = 'error';
      singleResult[0].message =
        err.response?.data?.message || err.message || 'Upload failed';
      setUploadResults([...singleResult]);
    }

    isUploadingRef.current = false;
    setIsUploading(false);

    if (onCompleteRef.current) {
      onCompleteRef.current();
    }
  }, []);

  const value = {
    isUploading,
    uploadResults,
    uploadProgressOpen,
    startFileUpload,
    startDocumentUpload,
    closeUploadProgress,
    registerOnComplete,
    unregisterOnComplete,
  };

  return (
    <DocumentUploadContext.Provider value={value}>
      {children}
      <UploadProgressModal
        isOpen={uploadProgressOpen}
        onRequestClose={closeUploadProgress}
        uploadResults={uploadResults}
        isUploading={isUploading}
      />
    </DocumentUploadContext.Provider>
  );
};
